import { syncQueryKey, useSync } from "#/lib/api/react-query/useSync"
import type { Ciphers } from "#/lib/api/types/Ciphers"
import { useSession } from "#/lib/auth/session-context"
import { decryptCiphers } from "#/lib/encryption/crypto"
import { useStretchedMasterKey } from "#/lib/encryption/master-key-context"
import { useQuery } from "@tanstack/react-query"

/**
 * useCiphers is a react hook that will return the decrypted ciphers
 */
export function useCiphers() {
  const { session } = useSession()
  const { data: cipherKeys } = useSync({
    query: {
      staleTime: Infinity,
      enabled: !!session,
      select(data) {
        return data.ciphers
      },
    },
  })
  const { stretchedMasterKey, isInitializing } = useStretchedMasterKey()

  return useQuery({
    queryKey: [syncQueryKey()[0], "decrypted", stretchedMasterKey, cipherKeys],
    queryFn: () => _decryptCiphers(stretchedMasterKey, cipherKeys),
    staleTime: Infinity,
    enabled: !!cipherKeys && !isInitializing && !!stretchedMasterKey,
  })
}

async function _decryptCiphers(
  stretchedMasterKey: CryptoKey | null,
  cipherKeys: Ciphers | undefined,
) {
  if (!cipherKeys || stretchedMasterKey === null) {
    console.error("Invalid state")
    // Won't happen due to the enabled check
    throw new Error("invalid state")
  }
  try {
    return await decryptCiphers(cipherKeys, stretchedMasterKey)
  } catch (error) {
    console.error("Decryption failed:", error)
    throw new Error("Decryption failed")
  }
}
